import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePortfolioData } from '../contexts/DataContext';
import { MapPin, Target, Lightbulb, ChevronDown, ChevronUp, Clock, Briefcase, Users } from 'lucide-react';

const getL = (obj, lang) => {
  if (!obj) return '';
  if (typeof obj === 'string') return obj;
  return obj[lang] || obj['pt-BR'] || '';
};

const Experience = () => {
  const { t, currentLanguage } = useLanguage();
  const { experiences, loading } = usePortfolioData();
  const [expandedCard, setExpandedCard] = useState(null);

  const clientsLabel = { 'pt-BR': 'Clientes', 'en': 'Clients', 'it': 'Clienti' };

  if (loading) return null;

  return (
    <section id="experience" data-testid="experience-section" className="py-20 bg-black">
      <div className="container max-w-7xl mx-auto px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-lime-400 uppercase tracking-tight mb-6">{t('experience.title')}</h2>
          <div className="w-24 h-1 bg-lime-400 mx-auto"></div>
        </div>
        <div className="space-y-8">
          {experiences.map((exp) => (
            <div key={exp.id} data-testid={`experience-card-${exp.id}`}
              className={`bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden hover:border-lime-400/30 transition-all duration-300 cursor-pointer ${expandedCard === exp.id ? 'border-lime-400/50' : ''}`}
              onClick={() => setExpandedCard(expandedCard === exp.id ? null : exp.id)}
            >
              <div className="p-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6 flex-1">
                    <div className="flex-shrink-0">
                      {exp.logo ? (
                        <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center p-2 shadow-lg">
                          <img src={exp.logo} alt={getL(exp.company, currentLanguage)} className="w-full h-full object-contain"
                            onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add('bg-lime-400'); e.target.parentElement.innerHTML = `<span class="text-black font-black text-xl">${getL(exp.company, currentLanguage).charAt(0)}</span>`; }}
                          />
                        </div>
                      ) : (
                        <div className="w-16 h-16 bg-lime-400 rounded-xl flex items-center justify-center shadow-lg">
                          <Briefcase className="text-black" size={28} />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl md:text-2xl font-bold text-white mb-1">{getL(exp.company, currentLanguage)}</h3>
                      <h4 className="text-base md:text-lg text-lime-400 font-semibold mb-3">{getL(exp.role, currentLanguage)}</h4>
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-6 text-gray-400 text-sm">
                        <div className="flex items-center space-x-2"><Clock size={14} /><span>{getL(exp.period, currentLanguage)}</span></div>
                        <div className="flex items-center space-x-2"><MapPin size={14} /><span>{getL(exp.location, currentLanguage)}</span></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-4 text-lime-400">
                    {expandedCard === exp.id ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                  </div>
                </div>
                <div className="mt-6 flex flex-wrap gap-2">
                  {(exp.technologies || []).map((tech, i) => (
                    <span key={i} className="px-3 py-1.5 bg-lime-400/10 border border-lime-400/30 text-lime-400 rounded-full text-xs font-medium">{tech}</span>
                  ))}
                </div>
                {exp.clients && exp.clients.length > 0 && (
                  <div className="mt-5">
                    <div className="flex items-center space-x-2 mb-3">
                      <Users size={14} className="text-gray-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{clientsLabel[currentLanguage]}</span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {exp.clients.map((client) => (
                        <div key={client.id || client.name} className="flex items-center space-x-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
                          {client.logo ? (
                            <img src={client.logo} alt={client.name} className="w-5 h-5 object-contain rounded" onError={(e) => { e.target.style.display = 'none'; }} />
                          ) : (
                            <div className="w-5 h-5 bg-gray-600 rounded flex items-center justify-center"><span className="text-[8px] text-white font-bold">{client.name.charAt(0)}</span></div>
                          )}
                          <span className="text-xs text-gray-300 font-medium">{client.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {expandedCard === exp.id && (
                <div className="border-t border-gray-800 bg-gray-950">
                  <div className="p-8">
                    <div className="mb-8 bg-gray-900 rounded-xl p-6 border border-gray-800">
                      <p className="text-gray-300 leading-relaxed">{getL(exp.description, currentLanguage)}</p>
                    </div>
                    {(exp.has_sub_projects || exp.hasSubProjects) && exp.subProjects && exp.subProjects.length > 0 && (
                      <div className="mb-8">
                        <h6 className="text-base md:text-lg font-bold text-lime-400 mb-6 uppercase tracking-wide flex items-center">
                          <Briefcase className="mr-3" size={20} />{t('experience.projects')}
                        </h6>
                        <div className="grid gap-6">
                          {exp.subProjects.map((sub) => (
                            <div key={sub.id} className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                              <div className="flex items-center space-x-3 mb-3">
                                <div className="w-2.5 h-2.5 bg-lime-400 rounded-full"></div>
                                <h6 className="text-base font-bold text-white">{getL(sub.client, currentLanguage)}</h6>
                              </div>
                              <p className="text-gray-300 mb-4 text-sm leading-relaxed">{getL(sub.description, currentLanguage)}</p>
                              <div className="flex flex-wrap gap-2">
                                {(sub.technologies || []).map((tech, i) => (
                                  <span key={i} className="px-2.5 py-1 bg-lime-400/5 border border-lime-400/20 text-lime-400 rounded-full text-xs">{tech}</span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <div className="flex items-center space-x-3 mb-4"><Target className="text-lime-400" size={20} /><h6 className="text-base font-bold text-lime-400 uppercase tracking-wide">{t('experience.challenges')}</h6></div>
                        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800"><p className="text-gray-300 text-sm leading-relaxed">{getL(exp.challenges, currentLanguage)}</p></div>
                      </div>
                      <div>
                        <div className="flex items-center space-x-3 mb-4"><Lightbulb className="text-lime-400" size={20} /><h6 className="text-base font-bold text-lime-400 uppercase tracking-wide">{t('experience.lessons')}</h6></div>
                        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800"><p className="text-gray-300 text-sm leading-relaxed">{getL(exp.lessons, currentLanguage)}</p></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
